import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, parseISO, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, Clock, User, Building2, 
  CheckCircle, XCircle, Circle, Filter, Users, List, CalendarDays
} from 'lucide-react';
import '@/calendar.css';

// Configurar localizador para español
const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Mensajes en español
const messages = {
  allDay: 'Todo el día',
  previous: 'Anterior',
  next: 'Siguiente',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Acción',
  noEventsInRange: 'No hay acciones en este rango.',
  showMore: total => `+ Ver más (${total})`
};

export default function Calendar({ user }) {
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  
  // Filter states
  const [selectedUserId, setSelectedUserId] = useState(user.id);
  const [selectedTypeId, setSelectedTypeId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  // Force list view on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('list');
      }
    };
    
    // Check on mount
    handleResize();
    
    // Add listener
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchActionTypes();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchActions();
  }, [selectedUserId, currentDate, currentView]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on current view
      let dateFrom, dateTo;
      if (currentView === 'month') {
        dateFrom = startOfMonth(currentDate).toISOString();
        dateTo = endOfMonth(currentDate).toISOString();
      } else if (currentView === 'week') {
        dateFrom = startOfWeek(currentDate, { locale: es }).toISOString();
        dateTo = new Date(startOfWeek(currentDate, { locale: es }).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      } else { // day
        dateFrom = startOfDay(currentDate).toISOString();
        dateTo = endOfDay(currentDate).toISOString();
      }

      const params = new URLSearchParams();
      if (selectedUserId !== 'all') {
        params.append('user_id', selectedUserId);
      }
      params.append('date_from', dateFrom);
      params.append('date_to', dateTo);

      const response = await axiosInstance.get(`/actions?${params.toString()}`);
      setActions(response.data);
    } catch (error) {
      toast.error('Error al cargar acciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const response = await axiosInstance.get('/action-types');
      setActionTypes(response.data);
    } catch (error) {
      console.error('Error loading action types');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users');
    }
  };

  const handleStatusChange = async (actionId, newStatus) => {
    try {
      await axiosInstance.patch(`/actions/${actionId}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      setDetailDialogOpen(false);
      fetchActions();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleSelectEvent = useCallback((event) => {
    setSelectedAction(event);
    setDetailDialogOpen(true);
  }, []);

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Preparar eventos para el calendario
  const events = actions
    .filter(action => {
      if (selectedTypeId !== 'all' && action.action_type_id !== selectedTypeId) return false;
      if (selectedStatus !== 'all' && action.status !== selectedStatus) return false;
      return true;
    })
    .map(action => {
      const actionType = actionTypes.find(t => t.id === action.action_type_id);
      const color = actionType?.color || 'gray';
      
      return {
        id: action.id,
        title: action.title,
        start: parseISO(action.scheduled_date),
        end: parseISO(action.scheduled_date),
        resource: action,
        className: `event-${color}`,
        statusClass: `event-${action.status}`
      };
    });

  // Estilo personalizado para eventos
  const eventStyleGetter = (event) => {
    const actionType = actionTypes.find(t => t.id === event.resource.action_type_id);
    const color = actionType?.color || 'gray';
    
    const colorMap = {
      blue: '#3b82f6',
      purple: '#a855f7',
      green: '#22c55e',
      orange: '#f97316',
      red: '#ef4444',
      gray: '#6b7280'
    };

    return {
      style: {
        backgroundColor: colorMap[color],
        borderRadius: '4px',
        opacity: event.resource.status === 'completed' ? 0.7 : 1,
        border: 'none',
        display: 'block'
      }
    };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', class: 'bg-yellow-100 text-yellow-800', icon: Circle },
      completed: { label: 'Completada', class: 'bg-green-100 text-green-800', icon: CheckCircle },
      not_done: { label: 'No Realizada', class: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.class}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-[#212B35] mb-2">Calendario de Acciones</h1>
            <p className="text-[#212B35]/70">Gestiona tu agenda comercial</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'outline'}
              onClick={() => setViewMode('calendar')}
              className={`hidden md:flex ${viewMode === 'calendar' ? 'bg-[#9C0F17] hover:bg-[#7A0C12]' : ''}`}
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Calendario
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-[#9C0F17] hover:bg-[#7A0C12]' : ''}
            >
              <List className="w-4 h-4 mr-2" />
              Lista
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4" />
                  Usuario
                </Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {user.role === 'admin' && (
                      <SelectItem value="all">Todos los usuarios</SelectItem>
                    )}
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Filter className="w-4 h-4" />
                  Tipo de Acción
                </Label>
                <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    {actionTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Circle className="w-4 h-4" />
                  Estado
                </Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="completed">Completadas</SelectItem>
                    <SelectItem value="not_done">No Realizadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold">{events.length}</p>
                </div>
                <CalendarIcon className="w-8 h-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pendientes</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {events.filter(e => e.resource.status === 'pending').length}
                  </p>
                </div>
                <Circle className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completadas</p>
                  <p className="text-2xl font-bold text-green-600">
                    {events.filter(e => e.resource.status === 'completed').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">No Realizadas</p>
                  <p className="text-2xl font-bold text-red-600">
                    {events.filter(e => e.resource.status === 'not_done').length}
                  </p>
                </div>
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Calendar or List View */}
        {viewMode === 'calendar' ? (
          <Card>
            <CardContent className="pt-6">
              <div style={{ height: '600px' }}>
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  culture="es"
                  messages={messages}
                  onSelectEvent={handleSelectEvent}
                  onNavigate={handleNavigate}
                  onView={handleViewChange}
                  view={currentView}
                  date={currentDate}
                  eventPropGetter={eventStyleGetter}
                  views={['month', 'week', 'day']}
                  tooltipAccessor={(event) => `${event.title} - ${event.resource.client_name}`}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {events.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-600">No hay acciones para mostrar</p>
                  </div>
                ) : (
                  events
                    .sort((a, b) => new Date(a.start) - new Date(b.start))
                    .map((event) => (
                      <div
                        key={event.resource.id}
                        onClick={() => handleSelectEvent(event)}
                        className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{event.title}</h3>
                              <Badge className="bg-blue-100 text-blue-800">
                                {event.resource.action_type_name}
                              </Badge>
                              {getStatusBadge(event.resource.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {format(event.start, "d 'de' MMMM yyyy, HH:mm", { locale: es })}
                              </div>
                              <div className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {event.resource.client_name}
                              </div>
                              {event.resource.assigned_to_name && (
                                <div className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {event.resource.assigned_to_name}
                                </div>
                              )}
                            </div>
                            {event.resource.description && (
                              <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                                {event.resource.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detail Dialog */}
        {selectedAction && (
          <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Detalle de Acción</DialogTitle>
                <DialogDescription>
                  {format(parseISO(selectedAction.resource.scheduled_date), "EEEE d 'de' MMMM yyyy, HH:mm", { locale: es })}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2">{selectedAction.resource.title}</h3>
                  <div className="flex gap-2 mb-3">
                    <Badge className="bg-blue-100 text-blue-800">
                      {selectedAction.resource.action_type_name}
                    </Badge>
                    {getStatusBadge(selectedAction.resource.status)}
                  </div>
                </div>

                {selectedAction.resource.description && (
                  <div>
                    <Label>Descripción</Label>
                    <p className="text-sm text-gray-600 mt-1">{selectedAction.resource.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Cliente</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <Button
                        variant="link"
                        className="p-0 h-auto text-[#9C0F17]"
                        onClick={() => navigate(`/clients/${selectedAction.resource.client_id}`)}
                      >
                        {selectedAction.resource.client_name}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label>Usuario Asignado</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <User className="w-4 h-4 text-gray-400" />
                      <p className="text-sm">{selectedAction.resource.assigned_user_name}</p>
                    </div>
                  </div>
                </div>

                {selectedAction.resource.status === 'pending' && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleStatusChange(selectedAction.resource.id, 'completed')}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Marcar Completada
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 text-red-600 hover:text-red-700"
                      onClick={() => handleStatusChange(selectedAction.resource.id, 'not_done')}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      No Realizada
                    </Button>
                  </div>
                )}

                {selectedAction.resource.status !== 'pending' && (
                  <div className="text-center py-2 text-sm text-gray-500">
                    Esta acción ya está {selectedAction.resource.status === 'completed' ? 'completada' : 'marcada como no realizada'}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
